import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import {
    DataGrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    getGridStringOperators,
} from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';

import { OnboardDevicesOnlinePeersResponse } from '../interfaces';
import { buildOnboardDevicesOnlineRows } from '../utils/onboardDevicesOnlineTable';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { useManageGrid } from '../hooks/useManageGrid';

interface OnboardDevicesOnlineTableGridProps {
    peers: OnboardDevicesOnlinePeersResponse;
    loading: boolean;
    error: string | null;
    onReload: () => void;
}

interface ToolbarProps {
    loading: boolean;
    onReload: () => void;
}

const ONLINE_PEERS_COLUMN_DEFS = [
    { key: 'deviceId', width: 180 },
    { key: 'name', width: 180 },

    { key: 'mainOnlineSince', width: 200 },
    { key: 'mainOnlineTime', width: 160 },
    { key: 'mainLastHandshake', width: 190 },
    { key: 'mainRx', width: 140 },
    { key: 'mainTx', width: 140 },
    { key: 'mainTotal', width: 140 },
    { key: 'mainAllowedIps', width: 260 },
    { key: 'mainIsOnline', width: 150 },

    { key: 'secondaryOnlineSince', width: 220 },
    { key: 'secondaryOnlineTime', width: 180 },
    { key: 'secondaryLastHandshake', width: 210 },
    { key: 'secondaryRx', width: 160 },
    { key: 'secondaryTx', width: 160 },
    { key: 'secondaryTotal', width: 170 },
    { key: 'secondaryAllowedIps', width: 280 },
    { key: 'secondaryIsOnline', width: 170 },
];

function Toolbar({ loading, onReload }: ToolbarProps) {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload} disabled={loading}>
                Reload
            </Button>
        </GridToolbarContainer>
    );
}

export const OnboardDevicesOnlineTableGrid = ({
    peers,
    loading,
    error,
    onReload,
}: OnboardDevicesOnlineTableGridProps) => {
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('devices/onboard-online');

    const rows = useMemo(() => {
        return buildOnboardDevicesOnlineRows(peers);
    }, [peers]);

    const columns: GridColDef[] = useMemo(() => {
        return ONLINE_PEERS_COLUMN_DEFS.map((col) => {
            return {
                field: col.key,
                headerName: transformKeyToTitle(col.key),
                width: columnsWidth[col.key] || col.width,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                filterOperators: getGridStringOperators(),
            };
        });
    }, [columnsWidth]);

    const isEmptyState = rows.length === 0 && !loading && !error;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load online peers: {error}
                </Alert>
            )}
            {isEmptyState && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No active peers found on both VPN servers.
                </Alert>
            )}
            {gridStateSynced && (
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <DataGrid
                        sx={{ height: '100%' }}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        slots={{
                            toolbar: () => <Toolbar loading={loading} onReload={onReload} />,
                        }}
                        disableRowSelectionOnClick
                        onCellKeyDown={onCellKeyDown}
                        filterModel={gridFilterModel}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        onColumnWidthChange={handleColumnWidthChange}
                        onColumnVisibilityModelChange={handleColumnVisibilityChange}
                        onFilterModelChange={handleFilterModelChange}
                        columnVisibilityModel={columnsConfig}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                    page: 0,
                                },
                            },
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};
